import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Calendar, Users, Clock, Music, ChevronRight, Star, TriangleAlert as AlertTriangle, Clock4, Tag, Lock, Zap, Mail, Check, X as XIcon } from 'lucide-react';
import { useUser } from '../context/UserContext';
import { supabase } from '../lib/supabase';
import { tournamentService, TournamentInvitation } from '../services/TournamentService';

interface Tournament {
  id: string;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  status: 'upcoming' | 'active' | 'completed';
  entry_fee: number | null;
  prize_pool_amount: number | null;
  prize_pool_currency: string | null;
  prize_description: string;
  max_participants: number | null;
  participant_count?: number;
  songs?: TournamentSong[];
  category_id?: string | null;
  is_private?: boolean;
  allow_pre_registration?: boolean;
  is_featured?: boolean;
  background_image_url?: string | null;
  category?: {
    name: string;
    color: string;
  };
}

interface TournamentSong {
  id: string;
  song_id: string;
  tournament_id: string;
  song?: {
    title: string;
    artist: string;
    cover_url: string;
  };
}

export const Tournaments: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userEntries, setUserEntries] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'active' | 'upcoming' | 'past' | 'private'>('active');
  const [loadedData, setLoadedData] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState<TournamentInvitation[]>([]);
  const [respondingTo, setRespondingTo] = useState<string | null>(null);

  useEffect(() => {
    if (!loadedData) {
      loadTournaments();
      if (user) {
        loadUserEntries();
        loadInvitations();
      }
    }
  }, [user, loadedData]);

  const getActualStatus = (dbStatus: string, startDate: string, endDate: string, regLocked?: boolean): 'upcoming' | 'active' | 'completed' => {
    const now = new Date();
    const end = new Date(endDate);
    if (now > end) return 'completed';
    if (dbStatus === 'active' || regLocked) return 'active';
    const start = new Date(startDate);
    if (now >= start) return 'active';
    return 'upcoming';
  };

  const loadTournaments = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: tournamentError } = await supabase
        .from('tournaments')
        .select(`
          *,
          category:tournament_categories (name, color),
          songs:tournament_songs (
            id,
            song_id,
            song:songs (
              title,
              artist,
              cover_url
            )
          )
        `)
        .order('start_date', { ascending: false });

      if (tournamentError) throw tournamentError;

      const entryCounts = [];

      if (data && data.length > 0) {
        for (const tournament of data) {
          const { count, error: countError } = await supabase
            .from('tournament_entries')
            .select('*', { count: 'exact', head: true })
            .eq('tournament_id', tournament.id);

          if (countError) throw countError;

          entryCounts.push({
            tournament_id: tournament.id,
            count: count || 0
          });
        }
      }

      let enrichedTournaments = data?.map(tournament => {
        const countEntry = entryCounts.find(e => e.tournament_id === tournament.id);
        const count = countEntry ? countEntry.count : 0;
        const actualStatus = getActualStatus(tournament.status, tournament.start_date, tournament.end_date, tournament.registration_locked);
        return {
          ...tournament,
          status: actualStatus,
          participant_count: count
        };
      }) || [];

      if (user?.id) {
        const isAdmin = user.role === 'admin';

        if (!isAdmin) {
          const userInvitations = await tournamentService.getUserInvitations(user.id);
          const invitedTournamentIds = new Set(userInvitations.map(inv => inv.tournament_id));
          const filteredTournaments = [];

          for (const tournament of enrichedTournaments) {
            if (!tournament.is_private) {
              filteredTournaments.push(tournament);
              continue;
            }

            if (invitedTournamentIds.has(tournament.id)) {
              filteredTournaments.push(tournament);
            }
          }

          enrichedTournaments = filteredTournaments;
        }
      } else {
        enrichedTournaments = enrichedTournaments.filter(t => !t.is_private);
      }

      setTournaments(enrichedTournaments);
      setLoadedData(true);
    } catch (error) {
      console.error('Error loading tournaments:', error);
      setError('Failed to load tournaments. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const loadUserEntries = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('tournament_entries')
        .select('tournament_id')
        .eq('user_id', user.id);

      if (error) throw error;

      setUserEntries(data?.map(entry => entry.tournament_id) || []);
    } catch (error) {
      console.error('Error loading user tournament entries:', error);
    }
  };

  const loadInvitations = async () => {
    if (!user?.id) return;
    try {
      const invitations = await tournamentService.getUserInvitations(user.id);
      setPendingInvitations(invitations.filter(inv => inv.status === 'pending'));
    } catch (err) {
      console.error('Error loading invitations:', err);
    }
  };

  const handleInvitationResponse = async (invitationId: string, tournamentId: string, status: 'accepted' | 'declined') => {
    try {
      setRespondingTo(invitationId);
      if (status === 'accepted' && user) {
        await tournamentService.acceptInvitationAndJoin(invitationId, tournamentId, user.id);
      } else {
        await tournamentService.respondToInvitation(invitationId, status);
      }
      setPendingInvitations(prev => prev.filter(inv => inv.id !== invitationId));
      if (status === 'accepted') {
        navigate(`/tournaments/${tournamentId}`);
      }
    } catch (err) {
      console.error('Error responding to invitation:', err);
    } finally {
      setRespondingTo(null);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  const getDateDifference = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffInDays = Math.round((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (diffInDays > 0) {
      return `${diffInDays} days left`;
    } else if (diffInDays < 0) {
      return `Ended ${Math.abs(diffInDays)} days ago`;
    } else {
      return 'Ends today';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-900/30 text-green-400">
            Active
          </span>
        );
      case 'upcoming':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-900/30 text-blue-400">
            Upcoming
          </span>
        );
      case 'completed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-gray-900/30 text-gray-400">
            Completed
          </span>
        );
      default:
        return null;
    }
  };

  const featuredTournaments = tournaments.filter(t => t.is_featured && t.status === 'active' && !t.is_private);
  const activeTournaments = tournaments.filter(t => t.status === 'active');
  const upcomingTournaments = tournaments.filter(t => t.status === 'upcoming' && !t.is_private);
  const completedTournaments = tournaments.filter(t => t.status === 'completed' && !t.is_private);
  const privateTournaments = tournaments.filter(t => t.is_private);

  const DEFAULT_BG = 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80';

  const TournamentCard: React.FC<{ tournament: Tournament; dimmed?: boolean }> = ({ tournament, dimmed = false }) => {
    const bgImage = tournament.background_image_url || DEFAULT_BG;
    return (
      <div
        key={tournament.id}
        className={`relative rounded-xl overflow-hidden cursor-pointer group transition-all ${dimmed ? 'opacity-70 hover:opacity-100' : ''}`}
        style={{ minHeight: 200 }}
        onClick={() => navigate(`/tournaments/${tournament.id}`)}
      >
        <div
          className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
          style={{ backgroundImage: `url(${bgImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/55 to-black/20" />

        <div className="relative z-10 p-5 flex flex-col justify-between" style={{ minHeight: 200 }}>
          <div>
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center flex-wrap gap-1.5 bg-black/40 backdrop-blur-md rounded-lg px-2 py-1.5 max-w-[calc(100%-2.5rem)]">
                {getStatusBadge(tournament.status)}
                {tournament.is_private && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-900/60 text-amber-300">
                    <Lock size={10} className="mr-1" />
                    Private
                  </span>
                )}
                {tournament.category && tournament.category.name.toLowerCase() !== 'private' && (
                  <span
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold"
                    style={{
                      backgroundColor: `${tournament.category.color}40`,
                      color: tournament.category.color
                    }}
                  >
                    <Tag size={10} className="mr-1" />
                    {tournament.category.name}
                  </span>
                )}
              </div>
              <div className="bg-black/40 backdrop-blur-md p-1.5 rounded-lg ml-2 flex-shrink-0">
                <Trophy size={16} className={dimmed ? 'text-white/60' : 'text-yellow-400'} />
              </div>
            </div>
            <h3 className="font-bold text-lg text-white leading-tight mb-1 drop-shadow">{tournament.title}</h3>
            <p className="text-white/75 text-sm line-clamp-2 drop-shadow-sm">{tournament.description}</p>
          </div>

          <div className="mt-4 pt-3 border-t border-white/10">
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-white/70">
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <Calendar size={11} />
                  {tournament.status === 'upcoming'
                    ? formatDate(tournament.start_date)
                    : `${formatDate(tournament.start_date)} – ${formatDate(tournament.end_date)}`}
                </span>
                <span className="flex items-center gap-1">
                  <Users size={11} />
                  {tournament.participant_count || 0}
                  {tournament.max_participants ? ` / ${tournament.max_participants}` : ''}
                </span>
                <span className="flex items-center gap-1">
                  <Trophy size={11} />
                  {tournament.prize_pool_amount
                    ? `${tournament.prize_pool_currency || '$'}${tournament.prize_pool_amount}`
                    : tournament.prize_description.substring(0, 20) + (tournament.prize_description.length > 20 ? '…' : '')}
                </span>
              </div>
              {userEntries.includes(tournament.id) ? (
                <span className="flex items-center gap-1 text-kod-accent font-medium">
                  <Star size={11} />
                  Entered
                </span>
              ) : (
                <span className="flex items-center gap-0.5 text-white/50 hover:text-white/80">
                  {tournament.status === 'completed' ? 'Results' : 'View'}
                  <ChevronRight size={12} />
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center opacity-5">
          <img
            src="/TSL_Logo.png"
            alt="Logo"
            className="w-[600px] h-[600px] object-contain"
          />
        </div>
        <div className="relative z-10 animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-kod-accent"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6 flex items-center text-red-500">
          <AlertTriangle size={20} className="mr-2 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {/* Featured Tournaments — always visible above tabs */}
      {featuredTournaments.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Star size={18} className="text-yellow-400" />
            <h2 className="text-lg font-bold text-yellow-400 uppercase tracking-wide">Featured</h2>
          </div>
          <div className="space-y-4">
            {featuredTournaments.map(tournament => (
              <div
                key={tournament.id}
                className="relative rounded-xl overflow-hidden cursor-pointer border border-yellow-500/30 group"
                onClick={() => navigate(`/tournaments/${tournament.id}`)}
              >
                {tournament.background_image_url && (
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                    style={{ backgroundImage: `url(${tournament.background_image_url})` }}
                  />
                )}
                <div className={`absolute inset-0 ${tournament.background_image_url ? 'bg-gradient-to-r from-black/85 via-black/60 to-black/30' : 'bg-gradient-to-r from-yellow-900/10 to-transparent'} pointer-events-none`} />

                <div className="relative z-10 p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div className="flex-1">
                      <div className="flex items-center flex-wrap gap-2 mb-3">
                        {getStatusBadge(tournament.status)}
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-yellow-900/30 text-yellow-400">
                          <Star size={10} className="mr-1" />
                          Featured
                        </span>
                        {tournament.category && (
                          <span
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold"
                            style={{
                              backgroundColor: `${tournament.category.color}20`,
                              color: tournament.category.color
                            }}
                          >
                            <Tag size={12} className="mr-1" />
                            {tournament.category.name}
                          </span>
                        )}
                        <div className="text-sm text-white/70">
                          {formatDate(tournament.start_date)} - {formatDate(tournament.end_date)}
                        </div>
                      </div>

                      <h3 className="text-2xl font-bold mb-2">{tournament.title}</h3>
                      <p className="text-white/80 mb-4 max-w-2xl">{tournament.description}</p>

                      <div className="flex flex-wrap gap-4 mb-4">
                        <div className="bg-kod-purple-800/70 rounded-lg px-3 py-2">
                          <div className="flex items-center text-white/70 text-sm mb-1">
                            <Trophy size={16} className="mr-2 text-kod-accent" />
                            Prize Pool
                          </div>
                          <div className="font-semibold">
                            {tournament.prize_pool_amount
                              ? `${tournament.prize_pool_currency || '$'}${tournament.prize_pool_amount}`
                              : tournament.prize_description}
                          </div>
                        </div>

                        <div className="bg-kod-purple-800/70 rounded-lg px-3 py-2">
                          <div className="flex items-center text-white/70 text-sm mb-1">
                            <Users size={16} className="mr-2 text-kod-accent" />
                            Participants
                          </div>
                          <div className="font-semibold">
                            {tournament.participant_count || 0}
                            {tournament.max_participants ? ` / ${tournament.max_participants}` : ''}
                          </div>
                        </div>

                        <div className="bg-kod-purple-800/70 rounded-lg px-3 py-2">
                          <div className="flex items-center text-white/70 text-sm mb-1">
                            <Clock size={16} className="mr-2 text-kod-accent" />
                            Time Remaining
                          </div>
                          <div className="font-semibold">
                            {getDateDifference(tournament.end_date)}
                          </div>
                        </div>

                        <div className="bg-kod-purple-800/70 rounded-lg px-3 py-2">
                          <div className="flex items-center text-white/70 text-sm mb-1">
                            <Music size={16} className="mr-2 text-kod-accent" />
                            Songs
                          </div>
                          <div className="font-semibold">
                            {tournament.songs?.length || 0} songs
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      {userEntries.includes(tournament.id) ? (
                        <button
                          className="btn-outline flex items-center"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/tournaments/${tournament.id}`);
                          }}
                        >
                          <Star size={18} className="mr-2 text-kod-accent" />
                          View My Entry
                        </button>
                      ) : (
                        <button
                          className="btn-primary flex items-center"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/tournaments/${tournament.id}`);
                          }}
                        >
                          <Trophy size={18} className="mr-2" />
                          Enter Tournament
                        </button>
                      )}
                    </div>
                  </div>

                  {tournament.songs && tournament.songs.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-sm font-medium text-white/70 mb-3">Featured Songs:</h4>
                      <div className="flex overflow-x-auto space-x-3 pb-3">
                        {tournament.songs.slice(0, 4).map(songEntry => (
                          songEntry.song && (
                            <div key={songEntry.id} className="flex-shrink-0 w-40">
                              <div className="bg-kod-purple-800 rounded-lg overflow-hidden hover:bg-kod-purple-700/70 transition-colors">
                                <img
                                  src={songEntry.song.cover_url || 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80'}
                                  alt={songEntry.song.title}
                                  className="w-full aspect-square object-cover"
                                />
                                <div className="p-2">
                                  <div className="font-medium truncate">{songEntry.song.title}</div>
                                  <div className="text-xs text-white/60 truncate">{songEntry.song.artist}</div>
                                </div>
                              </div>
                            </div>
                          )
                        ))}
                        {tournament.songs.length > 4 && (
                          <div className="flex-shrink-0 w-16 flex items-center justify-center">
                            <div className="text-sm text-white/70">+{tournament.songs.length - 4} more</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <ChevronRight size={24} className="absolute right-6 top-1/2 transform -translate-y-1/2 text-white/30" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending Invitations Banner */}
      {pendingInvitations.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Mail size={18} className="text-amber-400" />
            <h2 className="text-lg font-bold text-amber-400 uppercase tracking-wide">Invitations</h2>
            <span className="bg-amber-500/20 text-amber-400 text-xs font-bold px-2 py-0.5 rounded-full">
              {pendingInvitations.length}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pendingInvitations.map(inv => (
              <div key={inv.id} className="bg-kod-purple-800 border border-amber-500/30 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Lock size={14} className="text-amber-400" />
                  <span className="text-xs font-bold text-amber-400 uppercase">Private Tournament</span>
                </div>
                <h3 className="font-bold text-lg mb-1">{inv.tournament?.title || 'Tournament'}</h3>
                <div className="text-sm text-white/60 mb-4">
                  {inv.tournament?.start_date && inv.tournament?.end_date && (
                    <span>{new Date(inv.tournament.start_date).toLocaleDateString()} - {new Date(inv.tournament.end_date).toLocaleDateString()}</span>
                  )}
                  {inv.inviter?.username && (
                    <span className="ml-2">| Invited by {inv.inviter.username}</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleInvitationResponse(inv.id, inv.tournament_id, 'accepted')}
                    disabled={respondingTo === inv.id}
                    className="flex-1 flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium py-2.5 px-4 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <Check size={14} />
                    Accept
                  </button>
                  <button
                    onClick={() => handleInvitationResponse(inv.id, inv.tournament_id, 'declined')}
                    disabled={respondingTo === inv.id}
                    className="flex-1 flex items-center justify-center gap-1.5 bg-kod-purple-600 hover:bg-kod-purple-500 text-white/80 text-sm font-medium py-2.5 px-4 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <XIcon size={14} />
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Page title driven by tab */}
      <h1 className="text-2xl font-bold mb-6">
        {activeTab === 'active' ? 'Active Tournaments' :
         activeTab === 'upcoming' ? 'Upcoming Tournaments' :
         activeTab === 'past' ? 'Ended Tournaments' :
         'Private Tournaments'}
      </h1>

      {/* Tabs */}
      <div className="flex border-b border-kod-purple-600 mb-6">
        <button
          onClick={() => setActiveTab('active')}
          type="button"
          className={`px-4 py-3 font-medium relative ${
            activeTab === 'active'
              ? 'text-kod-accent'
              : 'text-white/70 hover:text-white/90'
          }`}
        >
          <div className="flex items-center">
            <Zap size={18} className="mr-2" />
            Active
            {activeTournaments.length > 0 && (
              <span className="ml-2 bg-green-900/40 text-green-400 text-xs px-1.5 py-0.5 rounded-full">
                {activeTournaments.length}
              </span>
            )}
          </div>
          {activeTab === 'active' && (
            <div className="absolute bottom-0 left-0 w-full h-0.5 bg-kod-accent"></div>
          )}
        </button>

        <button
          onClick={() => setActiveTab('upcoming')}
          type="button"
          className={`px-4 py-3 font-medium relative ${
            activeTab === 'upcoming'
              ? 'text-kod-accent'
              : 'text-white/70 hover:text-white/90'
          }`}
        >
          <div className="flex items-center">
            <Calendar size={18} className="mr-2" />
            Upcoming
            {upcomingTournaments.length > 0 && (
              <span className="ml-2 bg-kod-purple-700 text-white text-xs px-1.5 py-0.5 rounded-full">
                {upcomingTournaments.length}
              </span>
            )}
          </div>
          {activeTab === 'upcoming' && (
            <div className="absolute bottom-0 left-0 w-full h-0.5 bg-kod-accent"></div>
          )}
        </button>

        <button
          onClick={() => setActiveTab('past')}
          type="button"
          className={`px-4 py-3 font-medium relative ${
            activeTab === 'past'
              ? 'text-kod-accent'
              : 'text-white/70 hover:text-white/90'
          }`}
        >
          <div className="flex items-center">
            <Clock4 size={18} className="mr-2" />
            Ended
            {completedTournaments.length > 0 && (
              <span className="ml-2 bg-kod-purple-700 text-white text-xs px-1.5 py-0.5 rounded-full">
                {completedTournaments.length}
              </span>
            )}
          </div>
          {activeTab === 'past' && (
            <div className="absolute bottom-0 left-0 w-full h-0.5 bg-kod-accent"></div>
          )}
        </button>

        {privateTournaments.length > 0 && (
          <button
            onClick={() => setActiveTab('private')}
            type="button"
            className={`px-4 py-3 font-medium relative ${
              activeTab === 'private'
                ? 'text-kod-accent'
                : 'text-white/70 hover:text-white/90'
            }`}
          >
            <div className="flex items-center">
              <Lock size={18} className="mr-2" />
              Private
              <span className="ml-2 bg-kod-purple-700 text-white text-xs px-1.5 py-0.5 rounded-full">
                {privateTournaments.length}
              </span>
            </div>
            {activeTab === 'private' && (
              <div className="absolute bottom-0 left-0 w-full h-0.5 bg-kod-accent"></div>
            )}
          </button>
        )}
      </div>

      {/* Active Tournaments */}
      {activeTab === 'active' && activeTournaments.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {activeTournaments.map(tournament => (
            <TournamentCard key={tournament.id} tournament={tournament} />
          ))}
        </div>
      )}

      {activeTab === 'active' && activeTournaments.length === 0 && (
        <div className="card text-center py-12">
          <div className="inline-block bg-kod-purple-700/50 p-4 rounded-full mb-4">
            <Trophy size={36} className="text-kod-accent" />
          </div>
          <h2 className="text-xl font-bold mb-2">No Active Tournaments</h2>
          <p className="text-white/70 mb-6 max-w-lg mx-auto">
            There are no active tournaments right now. Check the Upcoming tab for competitions starting soon.
          </p>
        </div>
      )}

      {/* Upcoming Tournaments */}
      {activeTab === 'upcoming' && upcomingTournaments.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {upcomingTournaments.map(tournament => (
            <TournamentCard key={tournament.id} tournament={tournament} />
          ))}
        </div>
      )}

      {activeTab === 'upcoming' && upcomingTournaments.length === 0 && (
        <div className="card text-center py-12">
          <div className="inline-block bg-kod-purple-700/50 p-4 rounded-full mb-4">
            <Calendar size={36} className="text-white/50" />
          </div>
          <h2 className="text-xl font-bold mb-2">No Upcoming Tournaments</h2>
          <p className="text-white/70 mb-6 max-w-lg mx-auto">
            There are no upcoming tournaments scheduled at the moment. Check back soon for new competitions.
          </p>
        </div>
      )}

      {/* Past Tournaments */}
      {activeTab === 'past' && completedTournaments.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {completedTournaments.map(tournament => (
            <TournamentCard key={tournament.id} tournament={tournament} dimmed />
          ))}
        </div>
      )}

      {activeTab === 'past' && completedTournaments.length === 0 && (
        <div className="card text-center py-12">
          <div className="inline-block bg-kod-purple-700/50 p-4 rounded-full mb-4">
            <Clock4 size={36} className="text-white/50" />
          </div>
          <h2 className="text-xl font-bold mb-2">No Past Tournaments</h2>
          <p className="text-white/70 mb-6 max-w-lg mx-auto">
            There are no completed tournaments yet. Check the active or upcoming tournaments to participate.
          </p>
        </div>
      )}

      {/* Private Tournaments */}
      {activeTab === 'private' && privateTournaments.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {privateTournaments.map(tournament => (
            <TournamentCard key={tournament.id} tournament={tournament} />
          ))}
        </div>
      )}

      {activeTab === 'private' && privateTournaments.length === 0 && (
        <div className="card text-center py-12">
          <div className="inline-block bg-kod-purple-700/50 p-4 rounded-full mb-4">
            <Lock size={36} className="text-white/50" />
          </div>
          <h2 className="text-xl font-bold mb-2">No Private Tournaments</h2>
          <p className="text-white/70 mb-6 max-w-lg mx-auto">
            There are no private tournaments at the moment.
          </p>
        </div>
      )}

      {tournaments.length === 0 && !loading && !error && (
        <div className="card text-center py-12">
          <div className="inline-block bg-kod-purple-700/50 p-4 rounded-full mb-4">
            <Trophy size={36} className="text-white/50" />
          </div>
          <h2 className="text-xl font-bold mb-2">No Tournaments Found</h2>
          <p className="text-white/70 mb-6 max-w-lg mx-auto">
            There are no tournaments available at the moment. Check back soon for exciting singing competitions.
          </p>
        </div>
      )}
    </div>
  );
};
